import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const OfflineBanner: React.FC = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [justReconnected, setJustReconnected] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      setJustReconnected(false);
      setDismissed(false);
    };

    const handleOnline = () => {
      setIsOffline(false);
      setJustReconnected(true);
      const timer = setTimeout(() => {
        setJustReconnected(false);
      }, 3500);
      return () => clearTimeout(timer);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (dismissed && isOffline) return null;

  if (justReconnected) {
    return (
      <div className="bg-emerald-600 text-white text-xs font-semibold px-4 py-2 flex items-center justify-center gap-2 shadow-md transition-all animate-in fade-in duration-200">
        <Wifi className="w-4 h-4 text-emerald-200 shrink-0" />
        <span>
          {isFr ? 'Connexion Internet rétablie. Données actualisées.' : 'Internet connection restored. Live listings synced.'}
        </span>
      </div>
    );
  }

  if (isOffline) {
    return (
      <div className="bg-amber-900 text-amber-100 text-xs px-4 py-2.5 flex items-center justify-between gap-3 shadow-md border-b border-amber-800 animate-in slide-in-from-top-1 duration-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-amber-800 flex items-center justify-center shrink-0">
            <WifiOff className="w-3.5 h-3.5 text-amber-300" />
          </div>
          <div>
            <span className="font-bold text-white">
              {isFr ? 'Mode Hors Ligne (Réseau Indisponible)' : 'Offline Mode (Network Disconnected)'}
            </span>
            <span className="text-amber-200 text-[11px] block sm:inline sm:ml-2">
              {isFr
                ? 'Vous consultez les annonces mémorisées dans votre cache local.'
                : 'Browsing listings and photos preserved in your local device cache.'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-2.5 py-1 bg-amber-800 hover:bg-amber-700 text-amber-100 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
          >
            <RefreshCw className="w-3 h-3" />
            <span>{isFr ? 'Réessayer' : 'Retry'}</span>
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-1 text-amber-300 hover:text-white rounded-md transition-colors cursor-pointer"
            aria-label="Dismiss offline banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};
